(() => {
  const giftProperty = '_Chazoku subscription gift';
  const parentProperty = '_Chazoku subscription parent';
  const bundleProperty = '_Chazoku subscription bundle';
  const legacyParentProperty = 'Included gifts';
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const route = (path) => `${root}${path}`;
  let cleanAt = 0;
  let syncPromise = null;
  let syncTimer = null;
  let checkoutApproved = false;
  let bundleRemovalPromise = null;
  // Gift products are $0 SUB-GIFT items. Police them even when the hidden gift
  // property is missing (e.g. added straight through /cart/add.js).
  const isGift = (item) => item.properties?.[giftProperty] === 'true' ||
    /^subscription-gift-/.test(item.handle || '') || /^SUB-GIFT-/i.test(item.sku || '');

  // Inline, dismissible error instead of a silent page reload.
  const showError = (anchor, message) => {
    const host = anchor?.closest('form') || anchor?.parentElement || document.querySelector('cart-items');
    if (!host) return;
    let note = host.querySelector('[data-chazoku-cart-error]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-chazoku-cart-error', '');
      note.setAttribute('role', 'alert');
      note.style.cssText = 'margin:8px 0;padding:10px 12px;border:1px solid #b00020;border-radius:8px;color:#b00020;font-size:14px;line-height:1.4;background:#fff;';
      host.appendChild(note);
    }
    note.textContent = message;
  };

  const requestCart = async () => {
    const response = await fetch(route('cart.js'), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  };

  const refreshCart = () => {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.getCart === 'function') {
      cartItems.getCart();
      return;
    }
    if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  };

  const syncCart = ({refresh = true} = {}) => {
    if (syncPromise) return syncPromise;

    const syncStart = Date.now();
    syncPromise = (async () => {
      const cart = await requestCart();
      const parents = cart.items.filter((item) => {
        if (!item.selling_plan_allocation || isGift(item)) return false;
        return item.properties?.[parentProperty] === 'true' || Boolean(item.properties?.[legacyParentProperty]);
      });
      const parentBundles = new Set(
        parents
          .map((item) => item.properties?.[bundleProperty])
          .filter(Boolean),
      );
      const updates = {};

      // Keep subscription parents at the plan's delivery quantity (e.g. 2 x 35g),
      // so the first-order discount and gifts can't be taken on a smaller order.
      parents.forEach((item) => {
        const planQty = parseInt(item.properties?.['Delivery quantity'], 10);
        if (planQty > 0 && item.quantity !== planQty) updates[item.key] = planQty;
      });

      // One of each gift per subscription bundle; duplicates and orphans go to 0.
      const seenGifts = new Set();
      cart.items.forEach((item) => {
        if (!isGift(item)) return;
        const giftBundle = item.properties?.[bundleProperty];
        const hasParent = giftBundle ? parentBundles.has(giftBundle) : false;
        const giftKey = `${giftBundle || 'none'}:${item.variant_id}`;
        const keep = hasParent && !seenGifts.has(giftKey);
        if (keep) seenGifts.add(giftKey);
        updates[item.key] = keep ? 1 : 0;
        if (keep && item.quantity === 1) delete updates[item.key];
      });

      if (Object.keys(updates).length === 0) {
        if (syncStart > (window.__czCartMut || 0)) cleanAt = syncStart;
        return false;
      }

      const response = await fetch(route('cart/update.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({updates}),
      });
      if (!response.ok) throw new Error('Unable to update cart');
      await response.json();
      if (refresh) refreshCart();
      return true;
    })().finally(() => {
      syncPromise = null;
    });

    return syncPromise;
  };

  const scheduleSync = () => {
    cleanAt = 0;
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncCart().catch(() => {});
    }, 50);
  };

  const removeBundle = async (parentLine) => {
    if (bundleRemovalPromise) return bundleRemovalPromise;

    const bundleId = parentLine.dataset.chazokuBundle;
    if (!bundleId) return false;

    const bundleLines = [...document.querySelectorAll('[data-item][data-chazoku-bundle]')]
      .filter((line) => line.dataset.chazokuBundle === bundleId);
    const updates = {};

    bundleLines.forEach((line) => {
      updates[line.dataset.item] = 0;
      line.hidden = true;
      line.setAttribute('aria-hidden', 'true');
    });

    bundleRemovalPromise = fetch(route('cart/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({updates}),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to remove subscription bundle');
        return response.json();
      })
      .then(() => {
        refreshCart();
        return true;
      })
      .catch((error) => {
        bundleLines.forEach((line) => {
          line.hidden = false;
          line.removeAttribute('aria-hidden');
        });
        throw error;
      })
      .finally(() => {
        bundleRemovalPromise = null;
      });

    return bundleRemovalPromise;
  };

  // Fast path: if a sync already confirmed the cart after its last change, skip the extra cart.js round trip.
  const isClean = () => cleanAt > 0 && cleanAt > (window.__czCartMut || 0) && !syncPromise;
  const approveCheckout = async (button, form) => {
    try {
      if (!isClean()) await syncCart({refresh: false});
      checkoutApproved = true;
      if (button) {
        button.click();
      } else if (form) {
        form.submit();
      }
      window.setTimeout(() => {
        checkoutApproved = false;
      });
    } catch (error) {
      checkoutApproved = false;
      showError(button || form, 'We couldn\'t update your cart. Please check your connection and try checkout again.');
    }
  };

  document.addEventListener('theme:cart:load', scheduleSync);
  document.addEventListener('theme:cart:change', scheduleSync);
  window.addEventListener('pageshow', () => { cleanAt = 0; scheduleSync(); });

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-item-remove]');
    const parentLine = removeButton?.closest('[data-chazoku-subscription-parent][data-chazoku-bundle]');
    if (!parentLine) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    removeBundle(parentLine).catch(() => showError(removeButton, 'We couldn\'t remove this subscription. Please try again.'));
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[name="checkout"]');
    if (!button || checkoutApproved) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    approveCheckout(button, button.form);
  }, true);

  document.addEventListener('submit', (event) => {
    if (checkoutApproved || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    approveCheckout(event.submitter, event.target);
  }, true);

  scheduleSync();
})();
